import errorIcon from "$assets/images/error-page.svg?url";
import { Button } from "$components/button/button";
import { makeStyles } from "$components/mui-styles";
import { GET_VISIT_BY_ID_QUERY } from "$routes/patient/[:patientId]/inpatient/_lib/queries";
import { useQuery } from "@apollo/client";
import { Box, Typography } from "@material-ui/core";
import type { Visit } from "elephant-web/gql";
import { FormattedMessage, useIntl } from "react-intl";
import InpatientCard, {
  InpatientCardPlaceholder,
  InpatientCardPlaceholderContainer,
} from "./inpatient-card";

const useStyles = makeStyles((theme) => ({
  errorImg: {
    height: theme.spacing(14),
  },
}));

const Error = ({ onRetry }: { onRetry: () => void }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const errorImgAlt = formatMessage({
    id: "errorPage.errorImg.alt",
    defaultMessage: "Error",
  });

  return (
    <InpatientCardPlaceholderContainer>
      <Box display="flex" alignItems="center">
        <Box mt={2}>
          <img className={classes.errorImg} alt={errorImgAlt} src={errorIcon} />
        </Box>
        <Box ml={2}>
          <Typography variant="body2">
            <FormattedMessage
              id="inpatientCard.error.message"
              defaultMessage="Oops! Something went wrong fetching the current inpatient visit..."
            />
          </Typography>
          <Button onClick={() => onRetry()} variant="text">
            <FormattedMessage
              id="inpatientCard.error.retryBtn"
              defaultMessage="Try again"
            />
          </Button>
        </Box>
      </Box>
    </InpatientCardPlaceholderContainer>
  );
};

export interface CurrentInpatientCardProps {
  patientId?: string;
  visitId: string;
}

export const CurrentInpatientCard = ({
  patientId,
  visitId,
  ...props
}: CurrentInpatientCardProps) => {
  const {
    data,
    loading: visitLoading,
    error,
    refetch,
  } = useQuery<{ visitById: Visit }>(GET_VISIT_BY_ID_QUERY, {
    variables: { id: visitId },
  });

  if (visitLoading) {
    return <InpatientCardPlaceholder />;
  }

  if (error) {
    return <Error onRetry={refetch} />;
  }

  const { visitById } = data;
  const { admissionEvent } = visitById;

  // Hide legacy detention-only visits (no admission event)
  if (!admissionEvent) {
    return null;
  }

  return (
    <InpatientCard
      {...props}
      visitId={visitId}
      patientUuid={patientId}
      admissionEvent={admissionEvent}
    />
  );
};
